import styles from "../style.module.scss";
import React, { useState } from "react";
import { TableColumnsType, message, TableProps } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import { useNavigate, useLocation } from "react-router-dom";
import VLTable from "views/uikit/VLTable";
import { VLTableActionsConfig } from "views/uikit/VLTable/tableUtils";
import {
  useGetTasksApiV1TasksGet,
  useGetTaskFilterOptionsApiV1TasksFiltersGet,
  GetTasksApiV1TasksGetQueryParams,
} from "vl-api/vlComponents";
import {
  TaskDTO,
  TaskStatus,
  TaskType,
  TaskSortField,
  SortOrder as APISortOrder,
} from "vl-api/vlSchemas";
import StatusCell from "../../../uikit/VLTable/TableCells/StatusCell/StatusCell";
import TextWithTooltip from "views/components/TextWithTooltip";
import { CopyOutlined, StopOutlined } from "@ant-design/icons";
import { useAbortTaskApiV1TasksTaskIdAbortPost } from "vl-api/vlComponents";
import { canAbortTask } from "./taskManagerUtils";
import Modal from "views/atomComponents/Modal";
import DangerCircleIcon from "assets/icons/DangerCircleIcon";
import {
  getTaskNameByTaskType,
  getStatusColor,
  getTextByStatus,
  getLoadingByStatus,
  getHrefByTaskType,
  getTooltipByStatus,
} from "./taskManagerUtils";
import { updateURLWithFilters } from "./useTaskFilter";
import { copyToClipboard, isOnPremUser } from "helpers/utility/utilities";
import useTaskFilters from "./useTaskFilter";
import Moment from "react-moment";
import moment from "moment";
import { TableFiltersProps } from "views/uikit/VLTable/TableFilters/TableFilters";
import { useDeviceType } from "hooks/useDeviceType";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { USER_MANAGEMENT } from "helpers/constants/filters";
import { DatasetStatusEnum, EDatasetStatus } from "../../../../types";

export default function TaskManagerTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const { show: isUserManagementEnabled } =
    useFeatureFlagService(USER_MANAGEMENT);
  const { isLaptop } = useDeviceType();

  const getResponsiveWidth = (
    desktopWithFilters: string,
    laptopWithFilters: string,
  ) => {
    if (isLaptop) {
      return laptopWithFilters;
    }
    return desktopWithFilters;
  };

  const { filters: currentFilters, queryParams: apiQueryParams } =
    useTaskFilters(location.search);

  const [taskToAbort, setTaskToAbort] = useState<TaskDTO | null>(null);

  const abortMutation = useAbortTaskApiV1TasksTaskIdAbortPost({
    onSuccess: () => {
      message.success("Task aborted successfully");
      setTaskToAbort(null);
    },
    onError: (error: any) => {
      const detail =
        error?.detail || error?.payload?.detail || "Failed to abort task";
      message.error(
        typeof detail === "string" ? detail : "Failed to abort task",
      );
      setTaskToAbort(null);
    },
  });

  // Get filter options from backend API
  const filterOptionsResponse = useGetTaskFilterOptionsApiV1TasksFiltersGet({
    queryParams: {
      start_time_from: apiQueryParams.start_time_from,
      start_time_to: apiQueryParams.start_time_to,
    },
  });

  const filterOptions = filterOptionsResponse?.data;

  // Generate available filters using backend data
  const availableFilters = useTaskFilters(
    location.search,
    filterOptions,
  ).availableFilters;

  const tasksApiResponse = useGetTasksApiV1TasksGet(
    { queryParams: apiQueryParams },
    {
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
    },
  );

  const tableData = tasksApiResponse?.data?.tasks || [];
  const loading = tasksApiResponse?.isLoading || false;

  const handleTableFilterChange = async (
    filters: GetTasksApiV1TasksGetQueryParams,
  ) => {
    updateURLWithFilters(navigate, filters);
  };

  // Get current sort state from URL params
  const currentSortBy = apiQueryParams.sort_by as TaskSortField | undefined;
  const currentSortOrder = apiQueryParams.sort_order as
    | APISortOrder
    | undefined;

  const handleSortChange: TableProps<TaskDTO>["onChange"] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const sortBy = sorter.columnKey as TaskSortField;
      let sortOrder: APISortOrder;

      // Check if clicking on the same column that's already sorted
      if (currentSortBy === sortBy) {
        // Toggle between asc and desc
        sortOrder = currentSortOrder === "asc" ? "desc" : "asc";
      } else {
        // New column clicked - default to desc
        sortOrder = "desc";
      }

      updateURLWithFilters(
        navigate,
        {
          ...apiQueryParams,
          sort_by: sortBy,
          sort_order: sortOrder,
        },
        false,
      );
    } else if (!sorter || (Array.isArray(sorter) && sorter.length === 0)) {
      // Reset to default sort
      const { sort_by, sort_order, ...restParams } = apiQueryParams;
      updateURLWithFilters(
        navigate,
        {
          ...restParams,
          sort_by: "start_time" as TaskSortField,
          sort_order: "desc" as APISortOrder,
        },
        false,
      );
    }
  };

  const actionsConfig: VLTableActionsConfig<TaskDTO> = {
    items: (record) => [
      {
        key: "copy",
        label: "Copy Task ID",
        onClick: () =>
          copyToClipboard(record.task_id)
            .then(() => message.success(`Task ID ${record.task_id} copied`))
            .catch(() => message.error("Failed to copy task ID")),
        icon: <CopyOutlined />,
      },
      ...(canAbortTask(record.status, record.task_type)
        ? [
            {
              key: "abort",
              label: "Abort Task",
              onClick: () => setTaskToAbort(record),
              icon: <StopOutlined />,
              danger: true,
            },
          ]
        : []),
    ],
  };

  // Helper function to get sortOrder for a column
  const getSortOrder = (columnKey: TaskSortField): SortOrder | undefined => {
    if (currentSortBy === columnKey && currentSortOrder) {
      return currentSortOrder === "asc" ? "ascend" : "descend";
    }
    return undefined;
  };

  const columns: TableColumnsType<TaskDTO> = [
    {
      title: "Time",
      dataIndex: "start_time",
      key: "start_time",
      width: getResponsiveWidth("8%", "10%"),
      sorter: true,
      sortOrder: getSortOrder("start_time"),
      render: (dateString: string) => (
        <Moment format="M/D/YY, HH:mm" local={true} utc={true}>
          {dateString}
        </Moment>
      ),
    },
    {
      title: "Task",
      dataIndex: "task_type",
      key: "task_type",
      width: getResponsiveWidth("6%", "10%"),
      sorter: true,
      sortOrder: getSortOrder("task_type"),
      render: (taskType: TaskType) => (
        <TextWithTooltip text={getTaskNameByTaskType[taskType]} />
      ),
    },
    {
      title: "Dataset",
      dataIndex: "dataset_name",
      key: "dataset_name",
      width: getResponsiveWidth("12%", "8%"),
      sorter: true,
      sortOrder: getSortOrder("dataset_name"),
      render: (text: string, record: TaskDTO) => (
        <TextWithTooltip
          text={text}
          type="link"
          href={
            [DatasetStatusEnum.FATAL_ERROR, EDatasetStatus.ERROR].includes(
              record.dataset_status,
            )
              ? undefined
              : getHrefByTaskType[record.task_type as TaskType]?.(record)
          }
          strong={true}
        />
      ),
    },
    {
      title: "Dataset ID",
      dataIndex: "dataset_id",
      key: "dataset_id",
      width: getResponsiveWidth("14%", "8%"),
      ellipsis: true,
      sorter: true,
      sortOrder: getSortOrder("dataset_id"),
      render: (text: string) => <TextWithTooltip maxLines={1} text={text} />,
    },
    {
      title: "Job",
      dataIndex: "job_name",
      key: "job_name",
      width: getResponsiveWidth("10%", "9%"),
      sorter: true,
      sortOrder: getSortOrder("job_name"),
      render: (job: string) => <TextWithTooltip text={job} />,
    },
    {
      title: "Setup Name",
      dataIndex: "setup_name",
      key: "setup_name",
      width: getResponsiveWidth("9%", "10%"),
      sorter: true,
      sortOrder: getSortOrder("setup_name"),
      render: (setupName: string) => (
        <TextWithTooltip text={setupName} tooltipPlacement="top" />
      ),
    },
    {
      title: "Recipe List",
      dataIndex: "recipes",
      key: "recipeList",
      width: getResponsiveWidth("10%", "9%"),
      render: (recipes: string[]) => (
        <TextWithTooltip
          maxLines={1}
          text={recipes ? recipes.join(", ") : ""}
          tooltipPlacement="top"
        />
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      width: getResponsiveWidth("8%", "7%"),
      render: (_, record: TaskDTO) => {
        if (record.status === "PENDING") {
          return <span>Waiting...</span>;
        }
        const dateFormat = "d[d] h[h] m[m] s[s]";
        const startTime = record.actual_start_time || record.start_time;
        if (record.end_time) {
          return (
            <Moment
              trim={true}
              local={true}
              duration={startTime}
              date={record.end_time}
              format={dateFormat}
            />
          );
        }
        return (
          <Moment
            trim={true}
            format={dateFormat}
            duration={moment.utc(startTime).local()}
            interval={1000}
          />
        );
      },
    },
    ...(!isOnPremUser() || isUserManagementEnabled
      ? [
          {
            title: "User",
            dataIndex: "created_by",
            key: "created_by",
            width: getResponsiveWidth("11%", "14%"),
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder("created_by"),
            render: (createdBy: string) => (
              <TextWithTooltip
                maxLines={1}
                text={createdBy}
                tooltipPlacement="top"
              />
            ),
          },
        ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: getResponsiveWidth("10%", "10%"),
      sorter: true,
      sortOrder: getSortOrder("status"),
      render: (status: TaskStatus, record) => (
        <StatusCell
          color={getStatusColor(status)}
          text={getTextByStatus[status]?.(record.progress_percentage)}
          loading={getLoadingByStatus[status] || false}
          tooltip={getTooltipByStatus[status](
            record.status_message ?? record.error_message,
          )}
        />
      ),
    },
  ];

  const tableFilters: TableFiltersProps = {
    availableFilters,
    filters: currentFilters,
    onTableFilterChange: handleTableFilterChange,
  };

  return (
    <>
      <VLTable
        columns={columns}
        dataSource={tableData}
        loading={loading}
        actions={actionsConfig}
        tableFilters={tableFilters}
        size={"middle"}
        rowKey="task_id"
        localeEmptyText={loading ? "Loading..." : "No tasks found"}
        pagination={{
          total: tasksApiResponse.data?.total || 0,
          tooltip: "",
          desktopItemsLength: 18,
          laptopItemsLength: 10,
        }}
        onSortChange={handleSortChange}
      />
      {taskToAbort && (
        <Modal
          title="Abort Task"
          subtitle="Are you sure you want to abort this task?"
          description={`This will stop the running task for dataset "${taskToAbort.dataset_name}". The operation cannot be undone.`}
          titleIcon={
            <div className={styles.dangerIcon}>
              <DangerCircleIcon variant="filled" />
            </div>
          }
          primaryLoading={abortMutation.isPending}
          onPrimaryActionClick={() =>
            abortMutation.mutate({
              pathParams: { taskId: taskToAbort.task_id },
            })
          }
          onSecondaryActionClick={() => setTaskToAbort(null)}
        />
      )}
    </>
  );
}
